import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getDefaultLandingPage } from '../utils/landingPage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, Lock, Mail, Users } from 'lucide-react';
import { useCompany } from '../contexts/CompanyContext';

export default function Login() {
  const company = useCompany();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const loggedInUser = await login(loginForm.email, loginForm.password);
      toast.success('Welcome back!');
      navigate(getDefaultLandingPage(loggedInUser));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920)' }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-red-900/90 to-slate-900/90"></div>
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div>
            <img 
              src="/logo.png" 
              alt={company.company_name || 'TMS'} 
              className="h-16 object-contain"
            />
          </div>
          <div>
            <h1 className="text-4xl font-bold tracking-tight mb-4">
              Transportation Management System
            </h1>
            <p className="text-xl text-slate-200 leading-relaxed max-w-lg">
              Streamline your freight brokerage operations with our comprehensive TMS platform.
            </p>
          </div>
          <div className="text-sm text-slate-300">
            <p>{company.company_name}</p>
            <p>{[company.company_address, company.company_city, company.company_state, company.company_zip, company.company_country].filter(Boolean).join(', ')}</p>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <img 
              src="/logo.png" 
              alt={company.company_name || 'TMS'} 
              className="h-12 object-contain"
            />
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-200">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
                <Lock className="h-8 w-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold tracking-tight text-slate-900">Welcome Back</h2>
              <p className="text-slate-600 mt-2">Sign in to access your TMS dashboard</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
              <div>
                <Label htmlFor="login-email" className="text-slate-700">Email Address</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="you@company.com"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    required
                    className="pl-10 h-12"
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="login-password" className="text-slate-700">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="••••••••"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    required
                    className="pl-10 h-12"
                    data-testid="login-password-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-semibold"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-200 space-y-4">
              <div className="text-center">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-red-600 hover:text-red-700 hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot your password?
                </Link>
              </div>
              
              <p className="text-center text-sm text-slate-500">
                Need access? Contact your administrator to request an account.
              </p>
              
              {/* Customer Portal Link */}
              <Link to="/clientportal/login" className="block">
                <Button 
                  variant="outline" 
                  className="w-full h-12 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                  data-testid="client-portal-btn"
                >
                  <Users className="mr-2 h-5 w-5" />
                  Customer Portal
                </Button>
              </Link>
            </div>
          </div>

          <p className="text-center text-xs text-slate-400 mt-6">
            &copy; {new Date().getFullYear()} {company.company_name || 'TMS'}. All rights reserved.
            {' · '}
            <Link to="/privacy" className="hover:text-red-600 hover:underline" data-testid="login-privacy-link">
              Privacy Policy
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
